import {
  Button,
  CircularProgress,
  Stack,
  Switch,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import alqemiDataService from "../../../api/AlqemiDataService";
import { BiggerIcon } from "../../../common/components/fundamentals/Icon";
import Loader from "../../../common/components/fundamentals/Loader/Loader";
import OfferAutoUpdateTextField from "../../../common/components/fundamentals/OfferAutoUpdateTextField";
import { useSnackbar } from "../../../common/context/SnackbarProvider";
import { useUser } from "../../../common/context/UserProvider";
import { FullStack } from "../../../common/styles/Box.styles";
import { borderStyle, smallGap } from "../../../styles/consts";
import { hasSalesOrAdminAccess } from "../../../utils";
import { useOffer } from "../../context/OfferProvider";
import { useEmailFeedback } from "../../context/EmailFeedbackContext";
import { useStepNavigation } from "../../context/StepNavigationContext";
import { substituteHourlyRate } from "../../utils/substituteHourlyRate";
import CandidateSelectionRow from "../CandidateSelectionRow";
import StepNavigation from "../StepNavigation";
import EmailFeedbackModal from "../Components/EmailFeedbackModal";
import EmailKeywordAnalysis from "../EmailKeywordAnalysis";

export default function EmailReviewStep() {
  const { offer, setOffer } = useOffer();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { offerId } = useParams();
  const { setCandidateStep } = useStepNavigation();
  const { showSnackbar } = useSnackbar();
  const { user } = useUser();
  const {
    setOriginalEmail,
    setCurrentEmail,
    currentEmail,
    setShowFeedbackPopup,
    setPendingNavigation,
    resetFeedbackState,
  } = useEmailFeedback();

  const [displayedMatchEmail, setDisplayedMatchEmail] = useState("");
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const processedEmailRef = useRef<string | null>(null);
  const [language, setLanguage] = useState<"en" | "de">("en");
  const [isTranslating, setIsTranslating] = useState(false);
  const [userTriggeredLanguageChange, setUserTriggeredLanguageChange] =
    useState(false);
  const [isRefreshingKeywords, setIsRefreshingKeywords] = useState(false);

  useEffect(() => {
    const currentCandidate = searchParams.get("currentCandidate");

    const candidateExists = currentCandidate && offer?.matches?.find(
      (match) => match.candidate?.email === currentCandidate
    );

    if (candidateExists) {
      if (currentCandidate !== displayedMatchEmail) {
        setDisplayedMatchEmail(currentCandidate);
      }
    } else if (offer?.matches && offer.matches.length > 0) {
      const highestScoreMatch = offer.matches.find(
        (match) =>
          match.score ===
          Math.max(...(offer.matches?.map((m) => m.score || 0) || []))
      );
      const defaultEmail =
        highestScoreMatch?.candidate?.email ||
        offer.matches[0]?.candidate?.email ||
        "";
      if (defaultEmail) {
        setDisplayedMatchEmail(defaultEmail);
        setSearchParams({ currentCandidate: defaultEmail }, { replace: true });
      }
    }
  }, [searchParams, offer?.matches]);

  const displayedMatch = offer?.matches?.find(
    (match) => match.candidate?.email === displayedMatchEmail
  );

  useEffect(() => {
    if (displayedMatch?.candidate) {
      const isCandidate = user.email === displayedMatch.candidate.email;
      const isSupervisor =
        user.email === displayedMatch.candidate.supervisor_email;

      if (isCandidate) {
        navigate(
          `/alqemi/${offerId}/feedback?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`
        );
      } else if (!isSupervisor && !hasSalesOrAdminAccess(user)) {
        navigate(
          `/alqemi/${offerId}/review?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`
        );
      }
    }
  }, [
    displayedMatch,
    user.email,
    user.sales,
    navigate,
    offerId,
    displayedMatchEmail,
  ]);

  // email_language is detected and persisted by the backend when the email is
  // generated (and updated by the translate flow); matches predating that
  // field fall back to the default "en".
  useEffect(() => {
    if (displayedMatch?.email_language) {
      setLanguage(displayedMatch.email_language);
    }
  }, [displayedMatch?.email_language]);

  useEffect(() => {
    if (!userTriggeredLanguageChange || !displayedMatch?.id) {
      return;
    }

    const startTranslation = async () => {
      setIsTranslating(true);
      showSnackbar("Translating email...", false);

      try {
        await alqemiDataService.translateEmail(displayedMatch.id!, language);

        const pollInterval = setInterval(async () => {
          try {
            const result = await alqemiDataService.getMatch(
              displayedMatch.id!,
              displayedMatch.updated_at
            );

            if (result.data?.email_translation_status === "completed") {
              clearInterval(pollInterval);

              if (offer && setOffer) {
                const updatedOffer = {
                  ...offer,
                  matches: offer.matches?.map((match) =>
                    match.id === displayedMatch.id
                      ? {
                          ...match,
                          email: result.data.email,
                          email_language: result.data.email_language,
                          email_translation_status:
                            result.data.email_translation_status,
                          email_keyword_analysis: result.data.email_keyword_analysis ?? match.email_keyword_analysis,
                        }
                      : match
                  ),
                };
                setOffer(updatedOffer);
              }

              showSnackbar("Email translated successfully!", false);
              setIsTranslating(false);
              setUserTriggeredLanguageChange(false);
            } else if (result.data?.email_translation_status === "failed") {
              clearInterval(pollInterval);
              showSnackbar("Email translation failed. Please try again.", true);
              setIsTranslating(false);
              setUserTriggeredLanguageChange(false);
              setLanguage(language === "en" ? "de" : "en");
            }
          } catch (error) {
            console.error("Error polling translation status:", error);
          }
        }, 3000);

        // Timeout after 2 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (isTranslating) {
            showSnackbar("Translation timed out. Please try again.", true);
            setIsTranslating(false);
            setUserTriggeredLanguageChange(false);
          }
        }, 120000);
      } catch (error) {
        console.error("Error starting translation:", error);
        showSnackbar("Failed to start translation. Please try again.", true);
        setIsTranslating(false);
        setUserTriggeredLanguageChange(false);
        setLanguage(language === "en" ? "de" : "en");
      }
    };

    startTranslation();
  }, [userTriggeredLanguageChange, displayedMatch?.id, language]);

  const emailContent = displayedMatch?.email || "";

  const emailContentWithRates = substituteHourlyRate(
    emailContent,
    displayedMatch?.candidate?.rates,
  );

  const [originalEmailForMatch, setOriginalEmailForMatch] = useState<{
    matchId: string;
    email: string;
  } | null>(null);

  // Capture original email once per match so post-save updates don't reset the baseline.
  useEffect(() => {
    if (emailContentWithRates && displayedMatch?.id) {
      if (!originalEmailForMatch || originalEmailForMatch.matchId !== displayedMatch.id) {
        setOriginalEmailForMatch({
          matchId: displayedMatch.id,
          email: emailContentWithRates,
        });
        setOriginalEmail(emailContentWithRates);
        setCurrentEmail(emailContentWithRates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedMatch?.id, emailContentWithRates]);

  const localHasChanges = originalEmailForMatch
    ? originalEmailForMatch.email !== currentEmail && originalEmailForMatch.email.length > 0
    : false;

  const doNavigateNext = () => {
    setCandidateStep(displayedMatchEmail, 4);
    navigate(
      `/alqemi/${offerId}/feedback?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`
    );
  };

  const doNavigatePrevious = () => {
    setCandidateStep(displayedMatchEmail, 2);
    navigate(
      `/alqemi/${offerId}/cv-adaptation?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`
    );
  };

  const handleNext = () => {
    if (localHasChanges) {
      setPendingNavigation(() => doNavigateNext);
      setShowFeedbackPopup(true);
    } else {
      doNavigateNext();
    }
  };

  const handlePrevious = () => {
    if (localHasChanges) {
      setPendingNavigation(() => doNavigatePrevious);
      setShowFeedbackPopup(true);
    } else {
      doNavigatePrevious();
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(emailContentWithRates.toString());
    showSnackbar("Copied to clipboard!");
  };

  const handleCandidateClick = (email: string) => {
    setSearchParams({ currentCandidate: email }, { replace: true });
    setDisplayedMatchEmail(email);
  };

  const handleGenerateEmail = async () => {
    if (!displayedMatch?.id) {
      showSnackbar("No match selected", true);
      return;
    }

    if (displayedMatch?.email) {
      processedEmailRef.current = displayedMatch.email.substring(0, 100);
    } else {
      processedEmailRef.current = null;
    }

    setIsGeneratingEmail(true);
    showSnackbar("Generating email", false);

    try {
      const response = await alqemiDataService.generateEmail(displayedMatch.id);

      if (response && !response.success) {
        showSnackbar(
          response.message || "Failed to start email generation",
          true
        );
        setIsGeneratingEmail(false);
      }
    } catch (error) {
      showSnackbar("Failed to start email generation. Please try again.", true);
      setIsGeneratingEmail(false);
    }
  };

  useQuery({
    queryKey: [
      "email-poll",
      displayedMatch?.id,
      displayedMatch?.updated_at,
      isGeneratingEmail,
    ],
    queryFn: async () => {
      if (!isGeneratingEmail || !displayedMatch?.id) {
        return null;
      }

      const result = await alqemiDataService.getMatch(
        displayedMatch.id,
        displayedMatch.updated_at
      );

      const hasEmail = result.data?.email && result.data.email.length > 0;

      const emailHash = hasEmail ? result.data.email.substring(0, 100) : null;
      const isNewEmail = emailHash && emailHash !== processedEmailRef.current;

      if (hasEmail && isNewEmail) {
        processedEmailRef.current = emailHash;

        if (offer && setOffer) {
          const updatedOffer = {
            ...offer,
            matches: offer.matches?.map((match) =>
              match.id === displayedMatch.id
                ? {
                    ...match,
                    email: result.data.email,
                    email_keyword_analysis: result.data.email_keyword_analysis ?? match.email_keyword_analysis,
                  }
                : match
            ),
          };
          setOffer(updatedOffer);
        }

        showSnackbar("Email generation completed!", false, false);
        setIsGeneratingEmail(false);
        return result.data;
      }

      return null;
    },
    enabled: isGeneratingEmail,
    refetchInterval: isGeneratingEmail ? 5000 : false,
    retry: false,
  });

  const handleRefreshKeywords = async () => {
    if (!displayedMatch?.id) return;
    setIsRefreshingKeywords(true);
    try {
      const result = await alqemiDataService.refreshEmailKeywords(displayedMatch.id);
      if (result.success && result.email_keyword_analysis && offer && setOffer) {
        const updatedOffer = {
          ...offer,
          matches: offer.matches?.map((match) =>
            match.id === displayedMatch.id
              ? { ...match, email_keyword_analysis: result.email_keyword_analysis }
              : match
          ),
        };
        setOffer(updatedOffer);
      }
    } catch {
      showSnackbar("Failed to refresh keyword analysis", true);
    } finally {
      setIsRefreshingKeywords(false);
    }
  };

  const handleOpenFeedback = () => {
    setShowFeedbackPopup(true);
  };

  const handleFeedbackClose = () => {
    resetFeedbackState();
    if (displayedMatch?.id && emailContentWithRates) {
      setOriginalEmailForMatch({
        matchId: displayedMatch.id,
        email: emailContentWithRates,
      });
      setOriginalEmail(emailContentWithRates);
      setCurrentEmail(emailContentWithRates);
    }
  };

  return (
    <FullStack
      sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <Stack
        sx={{ padding: smallGap, gap: smallGap, overflow: "auto", flex: 1 }}
      >
        <CandidateSelectionRow
          matches={offer?.matches}
          displayedMatchEmail={displayedMatchEmail}
          onCandidateClick={handleCandidateClick}
          disabled={isTranslating || isGeneratingEmail}
          showDetailedScores={hasSalesOrAdminAccess(user)}
        />
        <Stack sx={{ gap: smallGap }}>
          {isGeneratingEmail || isTranslating ? (
            <FullStack sx={{ border: borderStyle, minHeight: "60vh" }}>
              <Loader />
            </FullStack>
          ) : (
            <OfferAutoUpdateTextField
              key={displayedMatchEmail}
              id="email-content"
              defaultValue={emailContentWithRates}
              matchEmail={displayedMatchEmail}
              multiline
              onContentChange={setCurrentEmail}
            />
          )}

          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: smallGap,
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              disabled={isTranslating || isGeneratingEmail}
              onClick={handleOpenFeedback}
            >
              Give feedback about email
            </Button>
            <Button
              variant="outlined"
              onClick={handleCopyToClipboard}
              disabled={isTranslating || isGeneratingEmail}
            >
              <BiggerIcon name="Copy" />
            </Button>
            <Button
              variant="outlined"
              disabled={isGeneratingEmail || isTranslating}
              onClick={handleGenerateEmail}
            >
              {isGeneratingEmail ? (
                <CircularProgress size={20} />
              ) : !emailContent || emailContent.trim().length === 0 ? (
                "Generate Email"
              ) : (
                "Regenerate Email"
              )}
            </Button>
            <Stack sx={{ alignItems: "center" }}>
              <Switch
                size="small"
                checked={language === "de"}
                onChange={(e) => {
                  const newLanguage = e.target.checked ? "de" : "en";
                  setLanguage(newLanguage);
                  setUserTriggeredLanguageChange(true);
                }}
                disabled={isTranslating || isGeneratingEmail || !emailContent}
              />
              <span style={{ fontSize: "0.65rem", marginTop: "-4px", opacity: isTranslating ? 0.5 : 1 }}>
                {isTranslating ? "..." : "EN/DE"}
              </span>
            </Stack>
          </Stack>

          {displayedMatch?.email_keyword_analysis && (
            <EmailKeywordAnalysis
              emailKeywordAnalysis={displayedMatch.email_keyword_analysis}
              onRefresh={handleRefreshKeywords}
              isRefreshing={isRefreshingKeywords}
            />
          )}
        </Stack>
      </Stack>

      <StepNavigation
        candidateEmail={displayedMatchEmail}
        onNext={handleNext}
        onPrevious={handlePrevious}
        nextDisabled={!displayedMatch || isGeneratingEmail || isTranslating}
        hasUnsavedChanges={localHasChanges}
      />

      <EmailFeedbackModal
        matchId={displayedMatch?.id || ""}
        onClose={handleFeedbackClose}
      />
    </FullStack>
  );
}
